import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import JobChatBox from '../shared/JobChatBox';
import NavHeader from "../shared/Nav-Header";
import UpdateStatus from '../shared/UpdateStatus';
import MakeLists from '../shared/MakeLists';
import styles from './job1.module.css';
import JobLists from "./JobLists";

const Job1 = () => {
    const { jobId } = useParams();
    const [job, setJob] = useState(null);
    const [applications, setApplications] = useState([]);
    const [applicantIds, setApplicantIds] = useState([]);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [skillNames, setSkillNames] = useState([]);
    const [lists, setLists] = useState([]);
    const [showMakeLists, setShowMakeLists] = useState(false);
    const [showJobLists, setShowJobLists] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [status, setStatus] = useState('');
    const [rejectionReasons, setRejectionReasons] = useState([]);
    const [selectedApplicants, setSelectedApplicants] = useState([]);     
    const [notification, setNotification] = useState('');
    const [statusPopupPosition, setStatusPopupPosition] = useState({ x: 0, y: 0 });
    const [selectedList, setSelectedList] = useState(null);
    const [listName, setListName] = useState('');
    const navigate = useNavigate();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(1); // Adjust as needed

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentApplications = applications.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(applications.length / itemsPerPage);

    // Convert selectedApplicants to a Set for O(1) lookups
    const selectedApplicantsSet = useMemo(() => new Set(selectedApplicants), [selectedApplicants]);

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    localStorage.setItem('notification', 'Please login first');
                    navigate('/recruiter/login');
                    return;
                }

                const response = await axios.get(`/api/recruiter/jobs/job-details/${jobId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                
                setJob(response.data.job);
                setApplications(response.data.applications);
                setSkillNames(response.data.skillNames);
            } catch (error) {
                console.error('Error fetching job details:', error);
                setNotification('Error fetching job details. Please try again.');
            }
        };

        fetchJobDetails();
    }, [jobId, navigate]);

    const handleSendMessageToAll = useCallback(() => {
        setIsChatOpen(prev => !prev);
    }, []);

    const handleMakeLists = useCallback(() => {
        setShowMakeLists(prev => !prev);
    }, []);

    const handleProfileClick = useCallback((applicationId) => {
        navigate(`/recruiter/applications/${applicationId}`);  
    }, [navigate]);

    // Handle checkbox clicks more reliably
    const handleApplicantSelect = useCallback((applicantId, e) => {
        e.stopPropagation();
        const stringId = String(applicantId);
        setSelectedApplicants(prev => {
            const newSet = new Set(prev);
            if (newSet.has(stringId)) {
                newSet.delete(stringId);
            } else {
                newSet.add(stringId);
            }
            return newSet;
        });
    }, []);

    // Handle row clicks - won't interfere with checkbox clicks
    const handleRowClick = useCallback((applicationId, e) => {
        // Only navigate if the click wasn't on a checkbox or status button
        if (!e.target.closest('input[type="checkbox"]') && !e.target.closest('button')) {
            handleProfileClick(applicationId);
        }
    }, [handleProfileClick]);


    const handleStatusClick = useCallback((event, application) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedApplication(application);
        setStatus(application.status);
        setRejectionReasons(application.rejectionReasons || []);    
        const buttonRect = event.target.getBoundingClientRect();
        setStatusPopupPosition({
            x: buttonRect.left + buttonRect.width / 2,
            y: buttonRect.bottom
        });
    }, []);

    const handleCloseUpdateStatus = useCallback(() => {
        setSelectedApplication(null);
    }, []);

    const handleJobLists = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`/api/recruiter/lists/all/${jobId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setLists(response.data.lists);
            setShowJobLists(prev => !prev);
        } catch (error) {
            console.error('Error fetching lists:', error.message);
        }
    }, [jobId]);

        const handleStatusChange = async (event) => {
            event.preventDefault(); // Add this line

        event.stopPropagation();
        const newStatus = event.target.value;
        setStatus(newStatus);

        if (newStatus !== 'not up to snuff') {
            setRejectionReasons([]);
        }

        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/recruiter/applications/update-status/${selectedApplication._id}`, {
                status: newStatus,
                rejectionReasons: newStatus === 'not up to snuff' ? rejectionReasons : []
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setNotification('Status updated successfully!');
            setApplications(applications.map(app => 
                app._id === selectedApplication._id 
                    ? { ...app, status: newStatus, rejectionReasons: newStatus === 'not up to snuff' ? rejectionReasons : [] } 
                    : app
            ));

            if (newStatus === 'accepted') {
                await axios.post('/api/email/send-email', {
                    to: selectedApplication.emailOrPhone,
                    subject: 'Application Accepted',
                    text: `Congratulations! Your application has been accepted to ${job.title} at ${job.company.name}.
                    Kindly review us at {ui company review url} if you found UI helpful.`
                });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            setNotification('Error updating status. Please try again.');
        }
    };

    const handleRejectionReasonsChange = (event) => {
        event.stopPropagation();
        const { value } = event.target;
        setRejectionReasons(prevReasons => 
            prevReasons.includes(value) 
                ? prevReasons.filter(reason => reason !== value) 
                : [...prevReasons, value]
        );
    };

    const handleSaveRejectionReasons = async (event) => {
        event.stopPropagation();
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/recruiter/applications/update-status/${selectedApplication._id}`, {
                status,
                rejectionReasons
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setNotification('Rejection reasons updated successfully!');
            setApplications(applications.map(app => 
                app._id === selectedApplication._id 
                    ? { ...app, rejectionReasons } 
                    : app
            ));
        } catch (error) {
            console.error('Error updating rejection reasons:', error);
            setNotification('Error updating rejection reasons. Please try again.');
        }
    };

        const handleCloseChat = () => {
        setIsChatOpen(false);
    };



    // Memoize the table rows to prevent unnecessary re-renders
    const renderTableRow = useCallback((application) => {
        const applicantId = String(application.applicantId?._id);
        const isSelected = selectedApplicantsSet.has(applicantId);
        
        return (
            <tr
                key={application._id}
                className={isSelected ? styles.selectedRow : ''}
                onClick={(e) => handleRowClick(application._id, e)}
            >
                <td onClick={() => handleProfileClick(application._id)}>
                    <div className={styles.tableProfileContainer}>
                        <img
                            title={application.name}
                            src={application.profilePicture 
                                ? application.profilePicture.replace('/upload/', '/upload/w_192,h_200,c_fill/') 
                                : '/default-profile.png'}
                            alt="Profile"
                            className={styles.tableProfilePicture}
                            loading="lazy"
                        />
                        <span className={styles.tablPofileName}>{application.name}</span>
                    </div>
                </td>
                <td 
                    onClick={() => handleProfileClick(application._id)} 
                    className={`${styles.statusSelect} ${styles[application.status.replace(/\s+/g, '-').toLowerCase()]}`}
                >
                    {application.status}
                </td>
                <td className={styles.statusCell}>
                    <button onClick={(event) => handleStatusClick(event, application)}>Status</button>
                </td>
                {showMakeLists && (
                    <td 
                        className={styles.checkboxCell}
                        onClick={(e) => {
                            if (e.target.type !== 'checkbox') {
                                handleApplicantSelect(application.applicantId?._id, e);
                            }
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleApplicantSelect(application.applicantId?._id, e)}
                        />
                    </td>
                )}
            </tr>
        );
    }, [handleProfileClick, handleStatusClick, handleApplicantSelect, showMakeLists, selectedApplicantsSet, handleRowClick]);


    // Pagination controls component
    const PaginationControls = useCallback(() => (
        totalPages > 1 && (
            <div className={styles.paginationControls}>
                <button 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                >
                    First
                </button>
                <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
                <button 
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    Last
                </button>
            </div>
        )
    ), [currentPage, totalPages]);

    return (
        <>
            <NavHeader/>
            <div className={styles.jobDetailsContainer}>
                {notification && <p className={styles.notification}>{notification}</p>}
                {job && (
                    <>
                        <div className={styles.jobDetailsHeader}>
                            <img 
                                src={job.company.logo 
                                    ? job.company.logo.replace('/upload/', '/upload/w_192,h_200,c_fill/') 
                                    : '/default-profile.png'}
                                alt="Company Logo" 
                                className={styles.companyLogo} 
                                loading="lazy"
                            />
                            <h2>{job.title}</h2>
                        </div>
                        <p><strong>📍Location:</strong> {job.location.split('\n').map((line, i, arr) => (
                            <span key={i}>{line}<br />{i < arr.length - 1 && <>{' '.repeat(1)}</>}</span>
                        ))}</p>
                        <p><strong>💼Description:</strong> {job.description.split('\n').map((line, i, arr) => (
                            <span key={i}>{line}<br />{i < arr.length - 1 && <>{' '.repeat(27)}</>}</span>
                        ))}</p>
                        <p><strong>✔✘Requirements:</strong> {job.requirements.split('\n').map((line, i, arr) => (
                            <span key={i}>{line}<br />{i < arr.length - 1 && <>{' '.repeat(32)}</>}</span>
                        ))}</p>
                        <p><strong>🎨🔧 Skills:</strong> {
                            skillNames.map((skill, index) => (
                                <span key={index} className={styles[`skill${index % 11}`]}>
                                    {skill}
                                </span>
                            ))
                        }</p>
                        <p><strong>🎁Benefits:</strong> {job.benefits.split('\n').map((line, i, arr) => (
                            <span key={i}>{line}<br />{i < arr.length - 1 && <>{' '.repeat(21)}</>}</span>
                        ))}</p>
                        <p><strong>🎓Number of Applicants:</strong> {applications.length}</p>
                        <h3>Applicants</h3>
                        <div className={styles.buttonsContainer}>
                            <button onClick={handleJobLists}>Job Lists</button>
                            <button onClick={handleMakeLists}>Make Lists</button>
                            <button onClick={handleSendMessageToAll}>Send Message to All Applicants</button>
                        </div> 
                    </>
                )}
                        {showMakeLists && (
                            <MakeLists
                                jobId={job._id}
                                applications={applications}
                                listName={listName}
                                setListName={setListName}
                                selectedList={selectedList}
                                setSelectedList={setSelectedList}
                                selectedApplicants={selectedApplicants}
                                setSelectedApplicants={setSelectedApplicants}
                            />
                        )}
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Actions</th>
                                {showMakeLists && <th>Select</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map(renderTableRow)}
                        </tbody>
                    </table>

                     <PaginationControls 
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    /> 

                    <div className={styles.statusUpdateContainerPopupPositioning}
                        style={{
                            top: `${statusPopupPosition.y}px`,
                            left: `${statusPopupPosition.x}px`
                        }}
                    >
                    {selectedApplication && (
                        <UpdateStatus
                            selectedApplication={selectedApplication}
                            status={status}
                            rejectionReasons={rejectionReasons}
                            handleStatusChange={handleStatusChange}
                            handleRejectionReasonsChange={handleRejectionReasonsChange}
                            handleSaveRejectionReasons={handleSaveRejectionReasons}
                            handleClose={handleCloseUpdateStatus}
                        />
                    )}
                    </div>

                    {isChatOpen && (
                        <JobChatBox
                            jobTitle={job.title}
                            jobId={job._id}
                            applicantIds={applicantIds}
                            onClose={handleCloseChat}
                        />
                    )}

                {showJobLists && (
                    <JobLists
                        jobId={job._id}
                        listsAll={lists}
                        setLists={setLists}
                        listName={listName}
                        setListName={setListName}
                        selectedList={selectedList}
                        setSelectedList={setSelectedList}
                        setNotification={setNotification}
                        applications={applications}
                        selectedApplicants={selectedApplicants}
                        setSelectedApplicants={setSelectedApplicants}
                        handleViewProfile={handleProfileClick}
                        handleApplicantSelect={handleApplicantSelect}
                    />
                )}
            </div>
        </>
    );
}

export default React.memo(Job1);